/**
 * Lightweight i18n dictionary for Yoshitaka Karate CMS.
 *
 * Strings are grouped by surface (`nav`, `login`, `dashboard`, etc.). Add new
 * keys in BOTH `en` and `es`; missing keys fall back to the key path itself in
 * dev so gaps are visible.
 */

export const TRANSLATIONS = {
  en: {
    // Public site navigation
    "nav.home": "Home",
    "nav.about": "About",
    "nav.programs": "Programs",
    "nav.schedule": "Schedule",
    "nav.news": "News",
    "nav.contact": "Contact",
    "nav.blog": "Blog",
    "nav.login": "Login",
    "nav.dashboard": "Dashboard",
    "nav.logout": "Logout",

    // Language switcher
    "lang.toggle_hint": "Language",
    "lang.en": "EN",
    "lang.es": "ES",

    // Login page
    "login.title": "Login",
    "login.subtitle": "Sign in to access your member portal.",
    "login.identifier_label": "Username, email, or member #",
    "login.identifier_placeholder": "e.g. johnsmith or john@example.com",
    "login.password_label": "Password",
    "login.submit": "Sign In",
    "login.forgot": "Forgot password?",
    "login.google": "Continue with Google",

    // Common buttons
    "btn.save": "Save",
    "btn.save_changes": "Save Changes",
    "btn.cancel": "Cancel",
    "btn.close": "Close",
    "btn.edit": "Edit",
    "btn.delete": "Delete",
    "btn.add": "Add",
    "btn.remove": "Remove",
    "btn.confirm": "Confirm",
    "btn.saving": "Saving…",

    // Dashboard tabs
    "tab.overview": "Overview",
    "tab.users": "Users",
    "tab.students": "Students",
    "tab.payments": "Payments",
    "tab.attendance": "Attendance",
    "tab.notify": "Notify",
    "tab.blog": "Blog",
    "tab.cms": "CMS",
    "tab.livepreview": "Live Preview",
    "tab.enquiries": "Enquiries",
    "tab.permissions": "Permissions",
    "tab.security": "Security",
    // Public page static copy
    "page.programs_eyebrow": "Programs",
    "page.programs_heading": "A curriculum shaped by patience.",
    "page.program_label": "Program",
    "page.schedule_eyebrow": "Weekly Schedule",
    "page.schedule_heading": "When we train.",
    "page.hours": "Hours",
    "page.email": "Email",
    "day.Monday": "Monday",
    "day.Tuesday": "Tuesday",
    "day.Wednesday": "Wednesday",
    "day.Thursday": "Thursday",
    "day.Friday": "Friday",
    "day.Saturday": "Saturday",
    "day.Sunday": "Sunday",
    "tab.idcard": "ID Card",

    // Dashboard headers
    "dash.super_admin_title": "Super Admin Control",
    "dash.admin_title": "Admin Portal",
    "dash.dojo_administration": "Dojo Administration",
    "dash.student_administration": "Student Administration",
    "dash.edit_idcards_shortcut": "Edit ID Cards",

    // Overview stats
    "stat.students": "Students",
    "stat.admins": "Admins",
    "stat.payments_due": "Payments Due",
    "stat.active_members": "Active Members",
    "stat.latest_payments": "Latest Payments",
    "stat.no_payments": "No payments yet.",
    "stat.open": "open",

    // Users panel
    "users.all_users": "All Users",
    "users.records": "records",
    "users.add_user": "Add User",
    "users.bill": "Bill",
    "users.col_name": "Name",
    "users.col_email": "Email",
    "users.col_username": "Username",
    "users.col_role": "Role",
    "users.col_belt": "Belt",
    "users.col_member_no": "Member No.",
    "users.col_status": "Status",
    "users.col_actions": "Actions",
    "users.active": "Active",
    "users.inactive": "Disabled",
    "users.no_users": "No users.",

    // Add User modal
    "adduser.title": "Add New User",
    "adduser.onboarding": "Onboarding",
    "adduser.section_account": "Account",
    "adduser.section_information": "Information",
    "adduser.full_name": "Full Name",
    "adduser.username": "Username",
    "adduser.username_hint": "Required. The login they'll type at sign-in.",
    "adduser.email": "Email",
    "adduser.email_hint": "Optional. Useful for password resets and notifications.",
    "adduser.email_placeholder": "(optional)",
    "adduser.password": "Starter Password",
    "adduser.password_hint": "At least 6 chars. User can change later.",
    "adduser.role": "Role",
    "adduser.phone": "Phone",
    "adduser.belt_rank": "Belt Rank",
    "adduser.dob": "Date of Birth",
    "adduser.address": "Address",
    "adduser.emergency_name": "Emergency Contact Name",
    "adduser.emergency_phone": "Emergency Contact Phone",
    "adduser.medical_notes": "Medical Notes",
    "adduser.medical_placeholder": "Allergies, injuries…",
    "adduser.internal_notes": "Internal Notes",
    "adduser.photo": "Photo",
    "adduser.photo_hint": "Optional. Use Capture for webcam + crop, or pick a file.",
    "adduser.capture_crop": "Capture / Crop",
    "adduser.or": "or",
    "adduser.create": "Create User",
    "adduser.creating": "Creating…",

    // User Drawer tabs
    "drawer.tab_profile": "Profile",
    "drawer.tab_info": "Information",
    "drawer.tab_idcard": "ID Card",
    "drawer.tab_security": "Security",

    // ID Card tab
    "idcard.template_label": "Template",
    "idcard.template_hint": "Pick a starting design. Saves instantly — per-user overrides stack on top.",
    "idcard.section_overrides": "Custom overrides (this user only)",
    "idcard.member_photo": "Member Photo",
    "idcard.qr_color": "QR Code Color",
    "idcard.background_image": "Card Background Image",
    "idcard.background_hint": "JPG/PNG under 1.5 MB. Overrides the template default.",
    "idcard.photo_size": "Photo Size",
    "idcard.qr_size": "QR Size",
    "idcard.background_size": "Background Size",
    "idcard.background_opacity": "Background Opacity",
    "idcard.background_nudge_x": "Background Nudge ↔ (mm)",
    "idcard.background_nudge_y": "Background Nudge ↕ (mm)",
    "idcard.live_preview": "Live Preview",

    // Payments tab
    "payments.bill_student": "Bill Student",
    "payments.select_student": "Select student…",
    "payments.new_invoice": "New Invoice",
    "payments.calendar": "Payment Calendar",
    "payments.hide_calendar": "Hide calendar",
    "payments.show_calendar": "Show calendar",
    "payments.col_student": "Student",
    "payments.col_description": "Description",
    "payments.col_amount": "Amount",
    "payments.col_due": "Due",
    "payments.col_status": "Status",
    "payments.no_payments": "No payments.",
    "payments.status_due": "Due",
    "payments.status_paid": "Paid",
    "payments.status_overdue": "Overdue",
    "payments.remind": "Remind",
    "payments.remind_title": "Send email reminder",
    "payments.mark_paid": "Mark Paid",
    "payments.reopen": "Reopen",

    // CMS panel
    "cms.per_template_defaults": "Per-template defaults",
    "cms.idcard_templates": "ID Card Templates",
    "cms.idcard_templates_hint": "Edit Student / Team Class / Sensei templates — title, kanji, colors, labels. Changes apply to every user assigned that template.",
    "cms.edit_templates": "Edit Templates",
    "cms.last_updated": "Last updated",
    "cms.edit_content": "Edit Content",
    "cms.content_json": "Content (JSON)",
    "cms.advanced_raw_json": "Advanced · View raw JSON",
    "cms.save_page": "Save Page",
    "cms.create": "Create",

    // Attendance (USB scanner sign-in)
    "att.title": "Attendance",
    "att.sign_in_members": "Sign In Members",
    "att.usb_scanner_active": "USB Scanner Active",
    "att.listening": "Listening…",
    "att.scan": "Scan",
    "att.recent_signins": "Recent Sign-Ins",
    "att.no_signins": "No sign-ins yet today. Scan a member's ID to begin.",
    "att.col_time": "Time",
    "att.col_member": "Member",
    "att.col_member_no": "Member No.",
    "att.col_belt": "Belt",
    "att.col_method": "Method",
    "att.member": "Member",
    "att.signed_in": "Signed in",
    "att.not_found": "We couldn't find a member for that scan.",
    "att.search_placeholder": "Search name / member #",
    "att.clear_filters": "Clear",
    "att.prev": "Prev",
    "att.next": "Next",
    "att.of": "of",
    "att.rows_per_page": "Rows",
    "att.last_7": "Last 7 days",
    "att.last_30": "Last 30 days",
    "att.last_90": "Last 90 days",
    "att.last_year": "Last 12 months",
    "att.all_time": "All time",

    // Notifications
    "notify.title": "Send Notification",
    "notify.compose": "Compose",
    "notify.recipients": "Recipients",
    "notify.all_users": "All Users",
    "notify.active_only": "Active Only",
    "notify.by_role": "By Role",
    "notify.mode.everyone": "Everyone",
    "notify.mode.one_person": "One Person",
    "notify.message": "Message",
    "notify.msg_title": "Title",
    "notify.body": "Body",
    "notify.send": "Send Notification",
    "notify.send_short": "Send",
    "notify.sending": "Sending…",
    "notify.pick_role": "— Pick a role —",
    "notify.pick_person": "— Pick a person —",
    "notify.sent_to": "Sent to",
    "notify.person": "person",
    "notify.people": "people",
    "notify.role.super_admin": "Super Admins",
    "notify.role.admin": "Admins",
    "notify.role.renshi": "Renshi",
    "notify.role.sensei": "Sensei",
    "notify.role.team_member": "Team Members",
    "notify.role.student": "Students",

    // Blog admin
    "blog.editorial": "Editorial",
    "blog.title": "Blog Posts",
    "blog.new_post": "New Post",
    "blog.no_posts": "No posts yet. Publish the first one.",
    "blog.empty_hint": "No posts yet. Click \"New Post\" to publish your first dojo update.",
    "blog.col_title": "Title",
    "blog.col_author": "Author",
    "blog.col_published": "Published",
    "blog.publish": "Publish",
    "blog.unpublish": "Unpublish",
    "blog.published": "Published",
    "blog.draft": "Draft",
    "blog.by": "by",
    "blog.edit": "Edit",
    "blog.new": "New",
    "blog.update_post": "Update Post",
    "blog.create_post": "Create Post",
    "blog.cover_image": "Cover image (optional)",
    "blog.excerpt": "Excerpt (short preview)",
    "blog.body": "Body",
    "blog.markdown_hint": "Markdown-flavored text. Line breaks become paragraphs.",
    "blog.published_visible": "Published (visible to students)",

    // Permissions
    "perm.title": "Role Permissions",
    "perm.hint": "Super admins always have every permission. Customize the rest below.",
    "perm.save": "Save Permissions",
    "perm.access_control": "Access Control",
    "perm.roles_and_permissions": "Roles & Permissions",
    "perm.loading": "Loading permissions…",
    "perm.loading_short": "Loading…",
    "perm.override": "override",
    "perm.hide_from_ui": "Hide from panel (still enforced in code)",
    "perm.all_hidden": "All permissions are hidden — restore some below.",
    "perm.hidden_permissions": "Hidden permissions (click to restore)",
    "perm.hidden_toast": "Permission hidden from the panel",
    "perm.shown_toast": "Permission restored",
    "perm.added_toast": "Custom tag added",
    "perm.deleted_toast": "Custom tag deleted",
    "perm.delete_confirm": "Delete custom tag \"{key}\"?",
    "perm.membership": "Membership",
    "perm.auto_deactivate_title": "Auto-deactivate inactive members",
    "perm.auto_deactivate_hint": "Members flagged 'Exempt from auto-deactivation' on their profile are always skipped. Super admins are never touched.",
    "perm.days": "Days",
    "perm.metric": "Trigger",
    "perm.metric.scan": "Attendance sign-in",
    "perm.metric.login": "Portal login",
    "perm.metric.either": "Whichever is more recent",
    "perm.run_now": "Run sweep now",
    "perm.disabled_note": "Auto-deactivation is currently OFF (days = 0).",
    "perm.autodeact_saved": "Auto-deactivation settings saved",
    "perm.autodeact_not_deployed": "This endpoint isn't on the backend yet — deploy the latest backend to enable auto-deactivation.",
    "perm.autodeact_run_confirm": "Run the auto-deactivation sweep now? Any non-exempt user who hasn't been active in the configured window will be set to inactive.",
    "perm.autodeact_ran": "Sweep complete — {n} member(s) deactivated.",
    "perm.record_keeping": "Record-keeping",
    "perm.custom_tags": "Custom permission tags",
    "perm.custom_tags_hint": "Labels-only. Useful to mark roles that have been granted access to physical resources (locker keys, kata scrolls, tournament passes). The backend does NOT enforce these.",
    "perm.custom_key_placeholder": "e.g. locker_key_access",
    "perm.custom_description_placeholder": "Description (optional)",
    "perm.add_tag": "Add tag",
    "perm.no_custom_tags": "No custom tags yet.",

    // Training overlay
    "training.tab_label": "Training",
    "training.help_heading": "Help",
    "training.heading": "Training",
    "training.start_tour": "Start guided walkthrough",
    "training.start_hint": "9 steps, ~30s — highlighted tooltips on each field.",
    "training.tip": "Tip: you can re-open this panel any time — your form input is preserved.",

    // Shortcut: Edit ID Cards
    "shortcut.pick_user": "Pick a user to edit their ID card",
    "shortcut.search_placeholder": "Search by name, username, or member #",
    "shortcut.no_match": "No users match that search.",

    // Home page
    "home.eyebrow": "Traditional Shotokan Karate",
    "home.hero_line1": "Forge Character. Refine",
    "home.hero_line2": "Spirit.",
    "home.hero_sub": "A dojo devoted to the enduring practice of Shotokan karate — where discipline shapes the body and kata shapes the soul.",
    "home.cta_learn_more": "Learn More",
    "home.cta_contact_us": "Contact Us",
  },

  es: {
    // Public site navigation
    "nav.home": "Inicio",
    "nav.about": "Nosotros",
    "nav.programs": "Programas",
    "nav.schedule": "Horario",
    "nav.news": "Noticias",
    "nav.contact": "Contacto",
    "nav.blog": "Blog",
    "nav.login": "Iniciar Sesión",
    "nav.dashboard": "Panel",
    "nav.logout": "Cerrar Sesión",

    // Language switcher
    "lang.toggle_hint": "Idioma",
    "lang.en": "EN",
    "lang.es": "ES",

    // Login
    "login.title": "Iniciar Sesión",
    "login.subtitle": "Accede a tu portal de miembro.",
    "login.identifier_label": "Usuario, correo o número de miembro",
    "login.identifier_placeholder": "ej. johnsmith o john@example.com",
    "login.password_label": "Contraseña",
    "login.submit": "Entrar",
    "login.forgot": "¿Olvidaste tu contraseña?",
    "login.google": "Continuar con Google",

    // Common buttons
    "btn.save": "Guardar",
    "btn.save_changes": "Guardar Cambios",
    "btn.cancel": "Cancelar",
    "btn.close": "Cerrar",
    "btn.edit": "Editar",
    "btn.delete": "Eliminar",
    "btn.add": "Agregar",
    "btn.remove": "Quitar",
    "btn.confirm": "Confirmar",
    "btn.saving": "Guardando…",

    // Dashboard tabs
    "tab.overview": "Resumen",
    "tab.users": "Usuarios",
    "tab.students": "Estudiantes",
    "tab.payments": "Pagos",
    "tab.attendance": "Asistencia",
    "tab.notify": "Notificar",
    "tab.blog": "Blog",
    "tab.cms": "CMS",
    "tab.livepreview": "Vista en vivo",
    "tab.enquiries": "Consultas",
    "tab.permissions": "Permisos",
    "tab.security": "Seguridad",
    // Public page static copy
    "page.programs_eyebrow": "Programas",
    "page.programs_heading": "Un plan de estudios formado con paciencia.",
    "page.program_label": "Programa",
    "page.schedule_eyebrow": "Horario Semanal",
    "page.schedule_heading": "Cuándo entrenamos.",
    "page.hours": "Horario",
    "page.email": "Correo electrónico",
    "day.Monday": "Lunes",
    "day.Tuesday": "Martes",
    "day.Wednesday": "Miércoles",
    "day.Thursday": "Jueves",
    "day.Friday": "Viernes",
    "day.Saturday": "Sábado",
    "day.Sunday": "Domingo",
    "tab.idcard": "Credencial",

    // Dashboard headers
    "dash.super_admin_title": "Control de Super Admin",
    "dash.admin_title": "Portal de Admin",
    "dash.dojo_administration": "Administración del Dojo",
    "dash.student_administration": "Administración de Estudiantes",
    "dash.edit_idcards_shortcut": "Editar Credenciales",

    // Overview stats
    "stat.students": "Estudiantes",
    "stat.admins": "Administradores",
    "stat.payments_due": "Pagos Pendientes",
    "stat.active_members": "Miembros Activos",
    "stat.latest_payments": "Últimos Pagos",
    "stat.no_payments": "Aún no hay pagos.",
    "stat.open": "pendientes",

    // Users panel
    "users.all_users": "Todos los Usuarios",
    "users.records": "registros",
    "users.add_user": "Agregar Usuario",
    "users.bill": "Facturar",
    "users.col_name": "Nombre",
    "users.col_email": "Correo",
    "users.col_username": "Usuario",
    "users.col_role": "Rol",
    "users.col_belt": "Cinturón",
    "users.col_member_no": "Núm. Miembro",
    "users.col_status": "Estado",
    "users.col_actions": "Acciones",
    "users.active": "Activo",
    "users.inactive": "Inactivo",
    "users.no_users": "Sin usuarios.",

    // Add User modal
    "adduser.title": "Agregar Nuevo Usuario",
    "adduser.onboarding": "Incorporación",
    "adduser.section_account": "Cuenta",
    "adduser.section_information": "Información",
    "adduser.full_name": "Nombre Completo",
    "adduser.username": "Nombre de Usuario",
    "adduser.username_hint": "Requerido. El nombre que usarán para iniciar sesión.",
    "adduser.email": "Correo",
    "adduser.email_hint": "Opcional. Útil para restablecer contraseña y notificaciones.",
    "adduser.email_placeholder": "(opcional)",
    "adduser.password": "Contraseña Inicial",
    "adduser.password_hint": "Mínimo 6 caracteres. El usuario podrá cambiarla después.",
    "adduser.role": "Rol",
    "adduser.phone": "Teléfono",
    "adduser.belt_rank": "Cinturón",
    "adduser.dob": "Fecha de Nacimiento",
    "adduser.address": "Dirección",
    "adduser.emergency_name": "Nombre Contacto de Emergencia",
    "adduser.emergency_phone": "Teléfono Contacto de Emergencia",
    "adduser.medical_notes": "Notas Médicas",
    "adduser.medical_placeholder": "Alergias, lesiones…",
    "adduser.internal_notes": "Notas Internas",
    "adduser.photo": "Foto",
    "adduser.photo_hint": "Opcional. Usa Capturar para webcam + recorte, o elige un archivo.",
    "adduser.capture_crop": "Capturar / Recortar",
    "adduser.or": "o",
    "adduser.create": "Crear Usuario",
    "adduser.creating": "Creando…",

    // User Drawer tabs
    "drawer.tab_profile": "Perfil",
    "drawer.tab_info": "Información",
    "drawer.tab_idcard": "Credencial",
    "drawer.tab_security": "Seguridad",

    // ID Card tab
    "idcard.template_label": "Plantilla",
    "idcard.template_hint": "Elige un diseño base. Se guarda al instante — las personalizaciones se aplican encima.",
    "idcard.section_overrides": "Personalización (solo este usuario)",
    "idcard.member_photo": "Foto del Miembro",
    "idcard.qr_color": "Color del Código QR",
    "idcard.background_image": "Imagen de Fondo",
    "idcard.background_hint": "JPG/PNG menor a 1.5 MB. Sobrescribe la plantilla.",
    "idcard.photo_size": "Tamaño de la Foto",
    "idcard.qr_size": "Tamaño del QR",
    "idcard.background_size": "Tamaño del Fondo",
    "idcard.background_opacity": "Opacidad del Fondo",
    "idcard.background_nudge_x": "Ajuste Fondo ↔ (mm)",
    "idcard.background_nudge_y": "Ajuste Fondo ↕ (mm)",
    "idcard.live_preview": "Vista Previa",

    // Payments
    "payments.bill_student": "Facturar Estudiante",
    "payments.select_student": "Selecciona estudiante…",
    "payments.new_invoice": "Nueva Factura",
    "payments.calendar": "Calendario de Pagos",
    "payments.hide_calendar": "Ocultar calendario",
    "payments.show_calendar": "Mostrar calendario",
    "payments.col_student": "Estudiante",
    "payments.col_description": "Descripción",
    "payments.col_amount": "Monto",
    "payments.col_due": "Vencimiento",
    "payments.col_status": "Estado",
    "payments.no_payments": "Sin pagos.",
    "payments.status_due": "Pendiente",
    "payments.status_paid": "Pagado",
    "payments.status_overdue": "Vencido",
    "payments.remind": "Recordar",
    "payments.remind_title": "Enviar recordatorio por correo",
    "payments.mark_paid": "Marcar Pagado",
    "payments.reopen": "Reabrir",

    // CMS panel
    "cms.per_template_defaults": "Valores por plantilla",
    "cms.idcard_templates": "Plantillas de Credencial",
    "cms.idcard_templates_hint": "Edita las plantillas Estudiante / Equipo / Sensei — título, kanji, colores, etiquetas. Los cambios se aplican a todos los usuarios asignados a esa plantilla.",
    "cms.edit_templates": "Editar Plantillas",
    "cms.last_updated": "Última actualización",
    "cms.edit_content": "Editar Contenido",
    "cms.content_json": "Contenido (JSON)",
    "cms.advanced_raw_json": "Avanzado · Ver JSON",
    "cms.save_page": "Guardar Página",
    "cms.create": "Crear",

    // Attendance
    "att.title": "Asistencia",
    "att.sign_in_members": "Registrar Miembros",
    "att.usb_scanner_active": "Escáner USB Activo",
    "att.listening": "Escuchando…",
    "att.scan": "Escanear",
    "att.recent_signins": "Registros Recientes",
    "att.no_signins": "Aún no hay registros hoy. Escanea la credencial de un miembro para comenzar.",
    "att.col_time": "Hora",
    "att.col_member": "Miembro",
    "att.col_member_no": "Núm. Miembro",
    "att.col_belt": "Cinturón",
    "att.col_method": "Método",
    "att.member": "Miembro",
    "att.signed_in": "Registrado",
    "att.not_found": "No encontramos un miembro para esa credencial.",
    "att.search_placeholder": "Buscar nombre / núm. miembro",
    "att.clear_filters": "Limpiar",
    "att.prev": "Ant.",
    "att.next": "Sig.",
    "att.of": "de",
    "att.rows_per_page": "Filas",
    "att.last_7": "Últimos 7 días",
    "att.last_30": "Últimos 30 días",
    "att.last_90": "Últimos 90 días",
    "att.last_year": "Últimos 12 meses",
    "att.all_time": "Todo el historial",

    // Notifications
    "notify.title": "Enviar Notificación",
    "notify.compose": "Redactar",
    "notify.recipients": "Destinatarios",
    "notify.all_users": "Todos los Usuarios",
    "notify.active_only": "Solo Activos",
    "notify.by_role": "Por Rol",
    "notify.mode.everyone": "Todos",
    "notify.mode.one_person": "Una Persona",
    "notify.message": "Mensaje",
    "notify.msg_title": "Título",
    "notify.body": "Cuerpo",
    "notify.send": "Enviar Notificación",
    "notify.send_short": "Enviar",
    "notify.sending": "Enviando…",
    "notify.pick_role": "— Elige un rol —",
    "notify.pick_person": "— Elige una persona —",
    "notify.sent_to": "Enviado a",
    "notify.person": "persona",
    "notify.people": "personas",
    "notify.role.super_admin": "Super Administradores",
    "notify.role.admin": "Administradores",
    "notify.role.renshi": "Renshi",
    "notify.role.sensei": "Sensei",
    "notify.role.team_member": "Equipo",
    "notify.role.student": "Estudiantes",

    // Blog admin
    "blog.editorial": "Editorial",
    "blog.title": "Publicaciones del Blog",
    "blog.new_post": "Nueva Publicación",
    "blog.no_posts": "Aún no hay publicaciones. Publica la primera.",
    "blog.empty_hint": "Aún no hay publicaciones. Haz clic en \"Nueva Publicación\" para compartir la primera novedad del dojo.",
    "blog.col_title": "Título",
    "blog.col_author": "Autor",
    "blog.col_published": "Publicado",
    "blog.publish": "Publicar",
    "blog.unpublish": "Despublicar",
    "blog.published": "Publicado",
    "blog.draft": "Borrador",
    "blog.by": "por",
    "blog.edit": "Editar",
    "blog.new": "Nueva",
    "blog.update_post": "Actualizar Publicación",
    "blog.create_post": "Crear Publicación",
    "blog.cover_image": "Imagen de portada (opcional)",
    "blog.excerpt": "Extracto (vista previa corta)",
    "blog.body": "Cuerpo",
    "blog.markdown_hint": "Texto con formato Markdown. Los saltos de línea se convierten en párrafos.",
    "blog.published_visible": "Publicado (visible para estudiantes)",

    // Permissions
    "perm.title": "Permisos por Rol",
    "perm.hint": "Los super administradores siempre tienen todos los permisos. Personaliza el resto abajo.",
    "perm.save": "Guardar Permisos",
    "perm.access_control": "Control de Acceso",
    "perm.roles_and_permissions": "Roles y Permisos",
    "perm.loading": "Cargando permisos…",
    "perm.loading_short": "Cargando…",
    "perm.override": "sobrescrito",
    "perm.hide_from_ui": "Ocultar del panel (sigue aplicándose en el código)",
    "perm.all_hidden": "Todos los permisos están ocultos — restaura alguno abajo.",
    "perm.hidden_permissions": "Permisos ocultos (haz clic para restaurar)",
    "perm.hidden_toast": "Permiso ocultado del panel",
    "perm.shown_toast": "Permiso restaurado",
    "perm.added_toast": "Etiqueta personalizada agregada",
    "perm.deleted_toast": "Etiqueta personalizada eliminada",
    "perm.delete_confirm": "¿Eliminar la etiqueta personalizada \"{key}\"?",
    "perm.membership": "Membresía",
    "perm.auto_deactivate_title": "Desactivación automática por inactividad",
    "perm.auto_deactivate_hint": "Los miembros marcados como 'Exento de desactivación automática' en su perfil siempre se omiten. Los super administradores nunca se tocan.",
    "perm.days": "Días",
    "perm.metric": "Disparador",
    "perm.metric.scan": "Escaneo de asistencia",
    "perm.metric.login": "Inicio de sesión",
    "perm.metric.either": "El más reciente",
    "perm.run_now": "Ejecutar ahora",
    "perm.disabled_note": "La desactivación automática está DESACTIVADA (días = 0).",
    "perm.autodeact_saved": "Configuración de desactivación guardada",
    "perm.autodeact_not_deployed": "Este endpoint aún no está en el backend — despliega la versión más reciente para habilitar la desactivación automática.",
    "perm.autodeact_run_confirm": "¿Ejecutar el barrido de desactivación ahora? Todo usuario no exento sin actividad reciente pasará a inactivo.",
    "perm.autodeact_ran": "Barrido completo — {n} miembro(s) desactivado(s).",
    "perm.record_keeping": "Registro",
    "perm.custom_tags": "Etiquetas de permisos personalizadas",
    "perm.custom_tags_hint": "Solo etiquetas. Útiles para marcar roles con acceso a recursos físicos (llaves de casillero, kata scrolls, pases de torneo). El backend NO las aplica.",
    "perm.custom_key_placeholder": "ej. locker_key_access",
    "perm.custom_description_placeholder": "Descripción (opcional)",
    "perm.add_tag": "Agregar etiqueta",
    "perm.no_custom_tags": "Aún no hay etiquetas personalizadas.",

    // Training overlay
    "training.tab_label": "Guía",
    "training.help_heading": "Ayuda",
    "training.heading": "Guía",
    "training.start_tour": "Iniciar recorrido guiado",
    "training.start_hint": "9 pasos, ~30s — sugerencias resaltadas en cada campo.",
    "training.tip": "Consejo: puedes abrir este panel en cualquier momento — tus datos se conservan.",

    // Shortcut
    "shortcut.pick_user": "Elige un usuario para editar su credencial",
    "shortcut.search_placeholder": "Buscar por nombre, usuario o núm. de miembro",
    "shortcut.no_match": "Ningún usuario coincide con la búsqueda.",

    // Home
    "home.eyebrow": "Karate Shotokan Tradicional",
    "home.hero_line1": "Forja Carácter. Refina el",
    "home.hero_line2": "Espíritu.",
    "home.hero_sub": "Un dojo dedicado a la práctica perdurable del karate Shotokan — donde la disciplina forma el cuerpo y el kata forma el alma.",
    "home.cta_learn_more": "Saber Más",
    "home.cta_contact_us": "Contáctanos",
  },
};
